import React, { useMemo, useState } from 'react';
import { useFetchTopCardCountQuery } from 'features/store';
import Loader from 'components/common/Loader';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Stage_filter, jobCard_page } from 'features/auth/userSlice';
import { arrayToQueryParam } from 'components/CardListing/State';

export default function CardsStageTable() {
  const { data: apiData, isLoading } = useFetchTopCardCountQuery({
    refetchOnMountOrArgChange: true,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [hoveredStage, setHoveredStage] = useState(null);

  // Stage configuration with colors and display names
  const stageConfig = {
    creation: { name: 'CREATION', color: '#3b82f6' }, // blue
    cutting: { name: 'CUTTING', color: '#a855f7' }, // purple
    secendory_process: { name: 'SEC PROCESS', color: '#6366f1' }, // indigo
    embroidary: { name: 'EMBROIDERY', color: '#ec4899' }, // pink
    production: { name: 'PRODUCTION', color: '#eab308' }, // yellow
    packing: { name: 'PACKING', color: '#22c55e' }, // green
    account: { name: 'BILLING', color: '#f97316' }, // orange
    payment: { name: 'PAYMENT', color: '#14b8a6' }, // teal
    dispatch: { name: 'DISPATCH', color: '#06b6d4' }, // cyan
    completed: { name: 'COMPLETED', color: '#10b981' }, // emerald
    digitizer: { name: 'DIGITIZERS', color: '#8b5cf6' }, // violet
    invalid: { name: 'INVALID', color: '#ef4444' }, // red
    null: { name: 'UNASSIGNED', color: '#9ca3af' }, // gray
    '': { name: 'UNASSIGNED', color: '#9ca3af' }, // gray
  };

  // All stages in order for display
  const allStages = [
    'creation',
    'cutting',
    'secendory_process',
    'embroidary',
    'production',
    'packing',
    'account',
    'payment',
    'dispatch',
    'completed',
  ];

  // Process API data and create stages data
  const stagesData = useMemo(() => {
    if (!apiData || !Array.isArray(apiData)) return [];

    // Create a map from API data
    const dataMap = {};
    apiData.forEach((item) => {
      const stageKey = item.current_stage === null ? 'null' : (item.current_stage || '');
      if (!dataMap[stageKey]) {
        dataMap[stageKey] = { cards: 0, qty: 0 };
      }
      dataMap[stageKey].cards += item.no_of_card || 0;
      dataMap[stageKey].qty += item.quantity || 0;
    });

    // Create stages array with all stages, including those with 0 values
    const stages = allStages.map((stageKey) => {
      const stageData = dataMap[stageKey] || { cards: 0, qty: 0 };
      const config = stageConfig[stageKey] || { name: stageKey.toUpperCase(), color: '#9ca3af' };
      
      return {
        stage: config.name,
        stageKey: stageKey,
        cards: stageData.cards,
        qty: stageData.qty,
        color: config.color,
      };
    });

    // Add digitizer stage if it exists in the data
    if (dataMap['digitizer']) {
      const digitizerConfig = stageConfig['digitizer'] || { 
        name: 'DIGITIZERS', 
        color: '#8b5cf6' 
      };
      stages.push({
        stage: digitizerConfig.name,
        stageKey: 'digitizer',
        cards: dataMap['digitizer'].cards,
        qty: dataMap['digitizer'].qty,
        color: digitizerConfig.color,
        isDigitizer: true, // Flag to identify digitizer stage
      });
    }

    // Add any additional stages from API that aren't in the standard list
    Object.keys(dataMap).forEach((stageKey) => {
      if (!allStages.includes(stageKey) && stageKey !== 'null' && stageKey !== '' && stageKey !== 'digitizer') {
        const config = stageConfig[stageKey] || { 
          name: stageKey.toUpperCase().replace(/_/g, ' '), 
          color: '#9ca3af' 
        };
        stages.push({
          stage: config.name,
          stageKey: stageKey,
          cards: dataMap[stageKey].cards,
          qty: dataMap[stageKey].qty,
          color: config.color,
        });
      }
    });

    return stages;
  }, [apiData]);

  const totalCards = useMemo(() => 
    stagesData.reduce((sum, stage) => sum + stage.cards, 0), 
    [stagesData]
  );
  
  const totalQty = useMemo(() => 
    stagesData.reduce((sum, stage) => sum + stage.qty, 0), 
    [stagesData]
  );

  // Handle stage click - navigate to jobcards page with stage filter
  const handleStageClick = (stageKey, isDigitizer = false) => {
    if (!stageKey || stageKey === 'null' || stageKey === '') {
      return; // Don't navigate for unassigned stages
    }

    let stageQuery;
    
    // For digitizer stage, use is_degitizer_stage=true instead of current_stage
    if (isDigitizer || stageKey === 'digitizer') {
      stageQuery = '&is_degitizer_stage=true';
    } else {
      // For regular stages, use current_stage
      stageQuery = arrayToQueryParam("current_stage", [stageKey]);
    }
    
    // Dispatch Redux actions to set the filter
    dispatch(jobCard_page(1)); // Reset to page 1
    dispatch(Stage_filter(stageQuery));
    
    // Navigate to jobcards page
    navigate('/jobcard');
  };

  if (isLoading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        marginBottom: '2rem',
        padding: '2rem',
      }}>
        <Loader />
      </div>
    );
  }

  return (
    <div style={{
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      marginBottom: '2rem',
    }}>
      {/* Header */}
      <div style={{
        padding: '0.75rem 1.5rem',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          color: '#1f2937',
          margin: 0,
          marginBottom: '0.25rem',
        }}>
          Cards Production Overview
        </h2>
        <p style={{
          fontSize: '0.875rem',
          color: '#6b7280',
          margin: 0,
        }}>
          Stage-wise cards and quantity tracking
        </p>
      </div>

      {/* Table Container */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
        }}>
          <thead>
            <tr style={{
              backgroundColor: '#f3f4f6',
              borderBottom: '1px solid #e5e7eb',
            }}>
              <th style={{
                padding: '1rem 1.5rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Stage
              </th>
              <th style={{
                padding: '1rem 1.5rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Cards
              </th>
              <th style={{
                padding: '1rem 1.5rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Quantity
              </th>
              <th style={{
                padding: '1rem 1.5rem',
                textAlign: 'center',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {stagesData.length > 0 ? (
              stagesData.map((stage, index) => {
                const isHovered = hoveredStage === (stage.stageKey || index);
                const isDigitizer = stage.isDigitizer || stage.stageKey === 'digitizer';
                return (
                <tr 
                  key={stage.stageKey || index}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    transition: 'background-color 0.15s',
                    cursor: stage.stageKey && stage.stageKey !== 'null' && stage.stageKey !== '' ? 'pointer' : 'default',
                  }}
                  onClick={() => handleStageClick(stage.stageKey, isDigitizer)}
                  onMouseEnter={(e) => {
                    if (stage.stageKey && stage.stageKey !== 'null' && stage.stageKey !== '') {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      setHoveredStage(stage.stageKey || index);
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    setHoveredStage(null);
                  }}
                >
                  <td style={{
                    padding: '1rem 1.5rem',
                    whiteSpace: 'nowrap',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                    }}>
                      <div style={{
                        width: '4px',
                        height: '40px',
                        borderRadius: '9999px',
                        backgroundColor: stage.color,
                        transform: isHovered ? 'scaleY(1)' : 'scaleY(0.5)',
                        transformOrigin: 'center',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}></div>
                      <span style={{
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: '#1f2937',
                      }}>
                        {stage.stage}
                      </span>
                    </div>
                  </td>
                  <td style={{
                    padding: '1rem 1.5rem',
                    textAlign: 'center',
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#1f2937',
                    }}>
                      {stage.cards}
                    </span>
                  </td>
                  <td style={{
                    padding: '1rem 1.5rem',
                    textAlign: 'center',
                  }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#dbeafe',
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#1e40af',
                    }}>
                      {stage.qty.toLocaleString()}
                    </span>
                  </td>
                  <td style={{
                    padding: '1rem 1.5rem',
                    textAlign: 'center',
                  }}>
                    {stage.cards > 0 ? (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: '#d1fae5',
                        color: '#065f46',
                      }}>
                        Active
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: '#f3f4f6',
                        color: '#4b5563',
                      }}>
                        Idle
                      </span>
                    )}
                  </td>
                </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" style={{
                  padding: '2rem 1.5rem',
                  textAlign: 'center',
                  color: '#6b7280',
                }}>
                  No data available
                </td>
              </tr>
            )}
          </tbody>
          <tfoot style={{
            backgroundColor: '#4f46e5',
            color: 'white',
          }}>
            <tr>
              <td style={{
                padding: '1rem 1.5rem',
                fontWeight: 'bold',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
              }}>
                Total
              </td>
              <td style={{
                padding: '1rem 1.5rem',
                textAlign: 'center',
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#4338ca',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                }}>
                  {totalCards}
                </span>
              </td>
              <td style={{
                padding: '1rem 1.5rem',
                textAlign: 'center',
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#4338ca',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                }}>
                  {totalQty.toLocaleString()}
                </span>
              </td>
              <td style={{
                padding: '1rem 1.5rem',
              }}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        padding: '1.5rem',
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
      }}>
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '0.25rem',
          }}>
            Total Cards
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
          }}>
            {totalCards}
          </div>
        </div>
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '0.25rem',
          }}>
            Total Quantity
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
          }}>
            {totalQty.toLocaleString()}
          </div>
        </div>
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '1rem',
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '0.25rem',
          }}>
            Active Stages
          </div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#1f2937',
          }}>
            {stagesData.filter(s => s.cards > 0).length}
          </div>
        </div>
      </div>
    </div>
  );
}
